import { lookAt, multiply, type Mat4 } from "./view";

/** The light eye's offset from the camera's target, in millimetres
 *  (`MapOutdoorRender.cpp:251-255`, `MapOutdoorCharacterShadow.cpp:88-90`),
 *  with y negated as every map placement is (`props.ts`'s `modelMatrix`). */
export const CHARACTER_SHADOW_LIGHT_OFFSET_MM: readonly [number, number, number] = [
  -1.732 * 12_500, 12_500, 2 * 1.732 * 12_500, // MapOutdoorRender.cpp:251-253
];
/** `D3DXMatrixOrthoRH(2550, 2550, 1, 15000)` (`MapOutdoorCharacterShadow.cpp:99`), in mm. */
export const CHARACTER_SHADOW_SPAN_MM = 25_500; // MapOutdoorCharacterShadow.cpp:99
const CHARACTER_SHADOW_NEAR_MM = 10; // MapOutdoorCharacterShadow.cpp:99
const CHARACTER_SHADOW_FAR_MM = 150_000; // MapOutdoorCharacterShadow.cpp:99
/** The target's edge at the default `SHADOW_LEVEL 3`, `SHADOW_ALL`
 *  (`PythonSystem.cpp:305`, `PythonBackground.cpp:106-110`). */
export const CHARACTER_SHADOW_TEXTURE_PX = 512; // PythonBackground.cpp:109
/** `TFACTOR 0xFF808080` selected as the whole colour (`MapOutdoorCharacterShadow.cpp:103-106`). */
export const CHARACTER_SHADOW_TEXTURE_FACTOR = 0x80 / 0xff; // MapOutdoorCharacterShadow.cpp:103
/** `SHADOW_APPLY_DISTANCE` (`InstanceBase.cpp:2019`), in mm. */
const SHADOW_APPLY_DISTANCE_MM = 25_000; // InstanceBase.cpp:2019
/** Below this a body in `RENDER_MODE_BLEND` casts nothing (`ActorInstanceRender.cpp:377-379`). */
const SHADOW_MIN_ALPHA = 0.5; // ActorInstanceRender.cpp:378

/** The light's view-projection for a camera looking at `targetMm`: the look-at
 *  `BeginRenderCharacterShadowToTexture` aims (`MapOutdoorCharacterShadow.cpp:
 *  86-99`) and `SetInverseViewAndDynamicShaodwMatrices` projects the terrain by
 *  (`MapOutdoorRender.cpp:243-259`), in WebGL clip space. */
export function characterShadowViewProj(
  targetMm: readonly [number, number, number],
  out: Mat4 = new Float32Array(16),
): Mat4 {
  const eye: [number, number, number] = [
    targetMm[0] + CHARACTER_SHADOW_LIGHT_OFFSET_MM[0],
    targetMm[1] + CHARACTER_SHADOW_LIGHT_OFFSET_MM[1],
    targetMm[2] + CHARACTER_SHADOW_LIGHT_OFFSET_MM[2],
  ];
  const view = lookAt(eye, targetMm, [0, 0, 1]);
  const projection = new Float32Array(16);
  const depth = CHARACTER_SHADOW_FAR_MM - CHARACTER_SHADOW_NEAR_MM;
  projection[0] = 2 / CHARACTER_SHADOW_SPAN_MM;
  projection[5] = 2 / CHARACTER_SHADOW_SPAN_MM;
  projection[10] = -2 / depth;
  projection[14] = -(CHARACTER_SHADOW_FAR_MM + CHARACTER_SHADOW_NEAR_MM) / depth;
  projection[15] = 1;
  return multiply(projection, view, out);
}

/** Where a world point lands in the shadow texture: `m_matDynamicShadowScale`'s
 *  half-scale and half-offset (`MapOutdoorLoad.cpp:434-436`) on the light's clip
 *  position, v up the way a WebGL framebuffer stores it. */
export function characterShadowTexel(
  viewProj: Mat4,
  pointMm: readonly [number, number, number],
): [number, number] {
  const [x, y, z] = pointMm;
  const clipX = viewProj[0] * x + viewProj[4] * y + viewProj[8] * z + viewProj[12];
  const clipY = viewProj[1] * x + viewProj[5] * y + viewProj[9] * z + viewProj[13];
  return [clipX * 0.5 + 0.5, clipY * 0.5 + 0.5];
}

/** Whether a body is drawn into the shadow map: `CInstanceBase::RenderToShadowMap`
 *  skips doors, buildings and anything 2500 units or more from the main
 *  instance (`InstanceBase.cpp:2004-2026`), and `CActorInstance` skips a
 *  blending body under half alpha (`ActorInstanceRender.cpp:375-385`). */
export function castsCharacterShadow(body: {
  readonly distanceMm: number;
  readonly alpha: number;
  readonly building: boolean;
  readonly door: boolean;
}): boolean {
  return !body.building && !body.door
    && body.distanceMm < SHADOW_APPLY_DISTANCE_MM
    && body.alpha >= SHADOW_MIN_ALPHA;
}

/** The character shadow target (`CreateCharacterShadowTexture`,
 *  `MapOutdoorCharacterShadow.cpp:20-62`): cleared white and aimed once per
 *  frame by `begin`, drawn into by each casting body between `bind` and
 *  `unbind`, and read by the next frame's terrain through `viewProj`, the
 *  matrix its finished picture was drawn with. */
export class CharacterShadowMap {
  readonly texture: WebGLTexture;
  /** The light matrix the texture's finished picture was drawn with. */
  readonly viewProj: Mat4 = new Float32Array(16);
  /** The light matrix this frame's casts are drawn with. */
  readonly capturing: Mat4 = new Float32Array(16);
  /** Whether `texture` holds a finished picture yet. */
  ready = false;
  private readonly framebuffer: WebGLFramebuffer;

  constructor(readonly gl: WebGL2RenderingContext, readonly sizePx = CHARACTER_SHADOW_TEXTURE_PX) {
    const texture = gl.createTexture();
    const framebuffer = gl.createFramebuffer();
    const depth = gl.createRenderbuffer();
    if (texture === null || framebuffer === null || depth === null) {
      throw new Error("the character shadow target could not be created");
    }
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, sizePx, sizePx, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); // StateManager.cpp:298
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); // StateManager.cpp:299
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); // MapOutdoorRenderHTP.cpp:637
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); // MapOutdoorRenderHTP.cpp:638
    gl.bindRenderbuffer(gl.RENDERBUFFER, depth);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, sizePx, sizePx);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depth);
    const complete = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (!complete) throw new Error("the character shadow target is incomplete");
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.texture = texture;
    this.framebuffer = framebuffer;
  }

  /** `BeginRenderCharacterShadowToTexture`: aim the light at the camera's
   *  target and clear the target white (`MapOutdoorCharacterShadow.cpp:72-160`). */
  begin(targetMm: readonly [number, number, number]): void {
    characterShadowViewProj(targetMm, this.capturing);
    this.bind();
    this.gl.clearColor(1, 1, 1, 1); // MapOutdoorCharacterShadow.cpp:140
    this.gl.clearDepth(1);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
    this.unbind();
  }

  /** Point the device at the target for one body's cast. */
  bind(): void {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
    this.gl.viewport(0, 0, this.sizePx, this.sizePx);
  }

  /** Hand the device back to the window (`EndRenderCharacterShadowToTexture`, `:161-177`). */
  unbind(): void {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    this.gl.viewport(0, 0, this.gl.drawingBufferWidth, this.gl.drawingBufferHeight);
  }

  /** The frame's casts are finished: the terrain reads this picture next. */
  end(): void {
    this.viewProj.set(this.capturing);
    this.ready = true;
  }
}
